import type { ErrorCode, ErrorEnvelope } from '@signapass/shared/errors';
import { readImpersonation } from './impersonation.js';

const BASE = '/api/v1';

/**
 * A failed request, carrying the stable code from API-CONTRACT.md §2.
 *
 * Callers switch on `code`, never on the message. The message is written for a
 * human and will be translated; the code is the contract.
 */
export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field-level messages from a validation failure, keyed by field name. */
  get fieldErrors(): Record<string, string[]> {
    const fields = this.details?.fields;
    return typeof fields === 'object' && fields !== null
      ? (fields as Record<string, string[]>)
      : {};
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /**
   * Required on every /loyalty/* write. Generated by the caller *before* the
   * network attempt so a retry after a timeout cannot double-stamp.
   */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /**
   * A file sent as the raw request body with its own type as `Content-Type` —
   * a receipt photo (features/GIFT-CARDS.md). Instead of `body`, never with it:
   * the JSON parser's size limit protects every other endpoint, and base64
   * would add a third to an upload on a counter's mobile connection.
   */
  file?: Blob;
}

/**
 * The single place the SPA talks to the API.
 *
 * Credentials are always included and no token is ever read or written by
 * script: auth is httpOnly cookies (AGENTS.md §5), which is also why there is
 * no Authorization header anywhere in this file.
 */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, idempotencyKey, signal, file } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (file) headers['Content-Type'] = file.type;
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  const send = () =>
    fetch(`${BASE}${path}`, {
      method,
      headers,
      // Without this the browser withholds the session cookie and every
      // authenticated call 401s.
      credentials: 'include',
      body: file ?? (body === undefined ? undefined : JSON.stringify(body)),
      signal,
    });

  let response = await send();

  /**
   * The merchant access token lives fifteen minutes (AGENTS.md §5); the refresh
   * token is what keeps them signed in past that. A 401 is therefore usually an
   * expired access token, not a signed-out merchant: rotate once and retry
   * once. The same idempotency key goes out again, so a retried write cannot
   * apply twice.
   */
  if (response.status === 401 && canRefresh(path) && (await refreshSession())) {
    response = await send();
  }

  /**
   * Every response, before anything else — features/PLATFORM-ADMIN.md.
   *
   * Here rather than in a component, so no screen can forget to check and none
   * has to. It runs on failures too: a 403 during a support session is exactly
   * when somebody needs to be reminded whose account they are in.
   */
  readImpersonation(response);

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const envelope = payload as ErrorEnvelope | null;
    const error = envelope?.error;
    throw new ApiError(
      error?.code ?? 'INTERNAL_ERROR',
      // The server's message is safe to show and is already translatable;
      // inventing one here would produce two sources of copy for one failure.
      error?.message ?? 'Something went wrong. Please try again.',
      response.status,
      error?.details,
      error?.requestId,
    );
  }

  return payload as T;
}

/**
 * Whether a 401 from this path is worth a refresh.
 *
 * Not the credential endpoints themselves — a wrong password is a 401 that a
 * refresh cannot fix, and a 401 from `/auth/refresh` retried would loop. Not
 * staff, customer or platform sessions either: none of them has a refresh
 * token, so asking would only add a round trip before the same failure.
 * `/auth/me` is the exception that matters most — it is the first call after a
 * merchant comes back to an idle tab.
 */
function canRefresh(path: string): boolean {
  if (path === '/auth/me') return true;
  return !/^\/(auth|platform|me|sync|loyalty\/(earn|lookup|redeem))(\/|$|\?)/.test(path);
}

let refreshing: Promise<boolean> | null = null;

/**
 * One rotation for however many requests are waiting on it.
 *
 * A dashboard page fires several queries at once, and an expired token fails
 * them all together. Rotating once per failure would present the same refresh
 * token several times — and the server treats a reused refresh token as theft
 * and revokes the whole family, signing the merchant out.
 */
function refreshSession(): Promise<boolean> {
  refreshing ??= acrossTabs(() =>
    fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'include' })
      .then((r) => r.ok)
      .catch(() => false),
  ).finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/**
 * The same rule between tabs, which share one refresh cookie.
 *
 * Two dashboard tabs whose access tokens expire together would otherwise both
 * present the same refresh token, and the second presentation reads as reuse.
 * Under a Web Lock the second tab's request leaves only after the first
 * rotation has landed, so the browser sends the *new* cookie and the second
 * rotation is an honest one. Without `navigator.locks` (very old browsers) the
 * in-tab sharing above still holds.
 */
async function acrossTabs<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) return fn();
  return await locks.request('signapass:auth-refresh', fn);
}

/**
 * A UUID v4 for an idempotency key.
 *
 * `crypto.randomUUID` needs a secure context, which localhost and https both
 * satisfy. The fallback exists for a merchant running the staff app against a
 * plain-http address on their own network, where losing idempotency entirely
 * would be far worse than a slightly weaker source of randomness.
 */
export function newIdempotencyKey(): string {
  // Read off a widened reference: `'randomUUID' in crypto` narrows the global
  // to `never` on the false branch, since the lib types declare the method as
  // always present.
  const webCrypto = globalThis.crypto as Crypto | undefined;
  if (typeof webCrypto?.randomUUID === 'function') return webCrypto.randomUUID();
  if (!webCrypto) throw new Error('Web Crypto is unavailable; cannot generate a key');

  const bytes = new Uint8Array(16);
  webCrypto.getRandomValues(bytes);
  bytes[6] = ((bytes[6] as number) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
